import { Badge } from "@hub/components/ui/badge";
import { Button } from "@hub/components/ui/button";
import { Input } from "@hub/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@hub/components/ui/select";
import type { OrgBot } from "@hub/lib/org-bots";
import { ALL_SWITCHES } from "@hub/lib/org-switches";
import {
  type OrgProfile,
  type OrgSignin,
  type OrgSwitches,
  validateOrgSettings,
  validateOrgSignin,
  validateOrgSlug,
  validateOrgSwitches,
} from "@hub/lib/org-types";
import { ApiError, apiFetch } from "@hub/ui/api";
import { relativeTime } from "@hub/ui/format";
import { InstanceTable } from "@hub/ui/InstanceTable";
import { OrgBotCreate } from "@hub/ui/OrgBotCreate";
import { OrgBotList } from "@hub/ui/OrgBotList";
import { OrgProfileForm } from "@hub/ui/OrgProfileForm";
import { ConsentBlock, SecretBlock } from "@hub/ui/OrgSigninBlocks";
import { OrgSwitchesForm } from "@hub/ui/OrgSwitchesForm";
import {
  emptyProfileForm,
  type ProfileForm,
  profileFormFrom,
  profileFromForm,
} from "@hub/ui/org-form";
import { showsClientId, signinForSave, signinHints } from "@hub/ui/org-signin";
import * as Tabs from "@radix-ui/react-tabs";
import { useCallback, useEffect, useState } from "react";

interface OrgDetail {
  slug: string;
  name: string;
  status: string;
  channel: string;
  pinnedVersion: string | null;
  instanceCount: number;
  customBots: OrgBot[];
  profile: OrgProfile;
  entitlements: OrgSwitches;
  settings: OrgSwitches;
  signin: OrgSignin;
  events: { action: string; actor: string; at: string; detail: string | null }[];
}

const DEFAULT_SIGNIN: OrgSignin = {
  provider: "microsoft",
  app: "shared",
  clientId: null,
  tenantId: null,
};

function Card({ title, id, children }: { title: string; id?: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-4 rounded-xl border border-border bg-card p-5">
      <h2 className="mb-4 text-base font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="text-sm">
      <label htmlFor={htmlFor} className="mb-1 block font-medium">
        {label}
      </label>
      {children}
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function errorText(err: unknown): string {
  return err instanceof ApiError || err instanceof Error ? err.message : "request failed";
}

const SECTION_GROUPS = [
  {
    id: "company",
    label: "Company",
    sections: [
      ["identity", "Basics"],
      ["profile", "Company details"],
      ["glossary", "Glossary"],
      ["brand", "Brand & voice"],
    ],
  },
  { id: "teams", label: "Teams", sections: [["teams", "Teams"]] },
  {
    id: "tools",
    label: "Features & tools",
    sections: [
      ["access", "Features"],
      ["integrations", "Integrations"],
    ],
  },
  { id: "bots", label: "Bots", sections: [["bots", "Bots"]] },
  { id: "signin", label: "Sign-in", sections: [["signin", "Sign-in"]] },
  {
    id: "operations",
    label: "Operations",
    sections: [
      ["releases", "Releases"],
      ["people", "People"],
      ["history", "History"],
    ],
  },
] as const;

/** `slug === null` is the "new organization" form. */
export function OrgPage({
  slug,
  nymblTenantId,
}: {
  slug: string | null;
  nymblTenantId: string | null;
}) {
  const editing = slug !== null;
  const [section, setSection] = useState("identity");
  const [detail, setDetail] = useState<OrgDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [slugInput, setSlugInput] = useState("");
  const [name, setName] = useState("");
  const [form, setForm] = useState<ProfileForm>(emptyProfileForm());
  // Fresh copies: never aliased to each other or to anything the API returned.
  const [switches, setSwitches] = useState<{ entitlements: OrgSwitches; settings: OrgSwitches }>(
    () => ({
      entitlements: structuredClone(ALL_SWITCHES),
      settings: structuredClone(ALL_SWITCHES),
    }),
  );
  const [signin, setSignin] = useState<OrgSignin>(DEFAULT_SIGNIN);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [channelChoice, setChannelChoice] = useState("early");
  const [pinInput, setPinInput] = useState("");
  const [releaseNote, setReleaseNote] = useState<string | null>(null);
  const [releaseError, setReleaseError] = useState<string | null>(null);

  const load = useCallback(
    async (refreshForm = true) => {
      if (slug === null) return;
      try {
        const r = await apiFetch<{ data: OrgDetail }>(`/api/orgs/${encodeURIComponent(slug)}`);
        const d = r.data;
        setDetail(d);
        if (refreshForm) {
          setName(d.name);
          // One name: the org name and the profile name come from the same value.
          setForm(profileFormFrom({ ...d.profile, name: d.name }));
          setSwitches({
            entitlements: structuredClone(d.entitlements),
            settings: structuredClone(d.settings),
          });
          setSignin({ ...d.signin });
        }
        setChannelChoice(d.channel);
        setLoadError(null);
      } catch (err) {
        setLoadError(errorText(err));
      }
    },
    [slug],
  );

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    setSaveError(null);
    setSaved(false);
    const profile = profileFromForm(form);
    if (!profile.ok) {
      setSection(
        profile.error.startsWith("teams") || profile.error.startsWith("duplicate team")
          ? "teams"
          : profile.error.startsWith("glossary")
            ? "glossary"
            : profile.error.startsWith("brand.") ||
                profile.error.startsWith("writingStyle") ||
                profile.error.startsWith("assistantName")
              ? "brand"
              : profile.error.startsWith("name")
                ? "identity"
                : "profile",
      );
      return setSaveError(profile.error);
    }
    const entitlements = validateOrgSwitches(switches.entitlements);
    if (!entitlements.ok) {
      setSection("access");
      return setSaveError(entitlements.error);
    }
    const settings = validateOrgSettings(switches.settings, entitlements.value);
    if (!settings.ok) {
      setSection("access");
      return setSaveError(settings.error);
    }
    const signinOut = validateOrgSignin(signinForSave(signin));
    if (!signinOut.ok) {
      setSection("signin");
      return setSaveError(signinOut.error);
    }
    const body = {
      name,
      profile: profile.value,
      entitlements: entitlements.value,
      settings: settings.value,
      signin: signinOut.value,
    };

    setSaving(true);
    try {
      if (slug === null) {
        const newSlug = validateOrgSlug(slugInput);
        if (!newSlug.ok) {
          setSection("identity");
          return setSaveError(newSlug.error);
        }
        await apiFetch("/api/orgs", {
          method: "POST",
          body: JSON.stringify({ slug: newSlug.value, ...body }),
        });
        window.location.href = `/orgs/${encodeURIComponent(newSlug.value)}`;
        return;
      }
      await apiFetch(`/api/orgs/${encodeURIComponent(slug)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      await load();
      setSaved(true);
    } catch (err) {
      setSaveError(errorText(err));
    } finally {
      setSaving(false);
    }
  }

  async function moveChannel() {
    if (slug === null) return;
    setReleaseError(null);
    setReleaseNote(null);
    try {
      const r = await apiFetch<{ data: { moved: number } }>(
        `/api/orgs/${encodeURIComponent(slug)}/channel`,
        { method: "POST", body: JSON.stringify({ channel: channelChoice }) },
      );
      setReleaseNote(`Moved ${r.data.moved} ${r.data.moved === 1 ? "box" : "boxes"}`);
      await load(false);
    } catch (err) {
      setReleaseError(errorText(err));
    }
  }

  async function setPin(pinnedVersion: string | null) {
    if (slug === null) return;
    setReleaseError(null);
    setReleaseNote(null);
    try {
      await apiFetch(`/api/orgs/${encodeURIComponent(slug)}/pin`, {
        method: "PATCH",
        body: JSON.stringify({ pinnedVersion }),
      });
      setReleaseNote(pinnedVersion ? `Pinned to ${pinnedVersion}` : "Unpinned");
      setPinInput("");
      await load(false);
    } catch (err) {
      setReleaseError(errorText(err));
    }
  }

  if (editing && detail === null) {
    return loadError ? (
      <p className="text-sm text-destructive">{loadError}</p>
    ) : (
      <p className="text-sm text-muted-foreground">Loading…</p>
    );
  }

  return (
    <div className="flex w-full max-w-4xl flex-col gap-6">
      <div>
        <a href="/orgs" className="text-xs text-muted-foreground hover:underline">
          All organizations
        </a>
        <h1 className="font-display text-2xl font-semibold tracking-tight">
          {detail ? detail.name : "New organization"}
        </h1>
      </div>

      {loadError ? (
        <p role="alert" className="text-sm text-destructive">
          Could not refresh organization: {loadError}
        </p>
      ) : null}
      <Tabs.Root
        value={
          SECTION_GROUPS.find((group) => group.sections.some(([id]) => id === section))?.id ??
          "company"
        }
        onValueChange={(value) => {
          const group = SECTION_GROUPS.find((item) => item.id === value);
          if (group) setSection(group.sections[0][0]);
        }}
      >
        <div className="overflow-x-auto border-b">
          <Tabs.List aria-label="Organization settings" className="flex flex-wrap gap-1">
            {SECTION_GROUPS.filter((group) => editing || group.id !== "operations").map((group) => (
              <Tabs.Trigger
                key={group.id}
                value={group.id}
                className="min-h-11 border-b-2 border-transparent px-4 text-sm font-medium text-muted-foreground hover:text-foreground data-[state=active]:border-primary data-[state=active]:text-foreground focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring"
              >
                {group.label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
        </div>
        <Tabs.Content
          value={
            SECTION_GROUPS.find((group) => group.sections.some(([id]) => id === section))?.id ??
            "company"
          }
          className="space-y-6 pt-5"
        >
          {SECTION_GROUPS.filter(
            (group) => group.sections.some(([id]) => id === section) && group.sections.length > 1,
          ).map((group) => (
            <nav
              key={group.id}
              aria-label={`${group.label} sections`}
              className="flex flex-wrap gap-2"
            >
              {group.sections.map(([id, label]) => (
                <Button
                  key={id}
                  type="button"
                  size="sm"
                  variant={section === id ? "secondary" : "ghost"}
                  aria-pressed={section === id}
                  onClick={() => setSection(id)}
                >
                  {label}
                </Button>
              ))}
            </nav>
          ))}
          <p className="text-sm text-muted-foreground">
            Changes to organization settings are saved together. Release controls apply separately.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" onClick={save} disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Create organization"}
            </Button>
            {saved ? (
              <span role="status" className="text-sm text-muted-foreground">
                Saved.
              </span>
            ) : null}
            {saveError ? (
              <span role="alert" className="text-sm text-destructive">
                {saveError}
              </span>
            ) : null}
          </div>
          {section === "identity" ? (
            <Card id="identity" title="Basics">
              <div className="flex flex-col gap-4">
                <Field
                  label="Organization ID"
                  htmlFor="org-slug"
                  hint="A permanent identifier for this organization. Use lowercase letters, numbers and hyphens."
                >
                  <Input
                    id="org-slug"
                    className="max-w-md"
                    value={editing ? (slug ?? "") : slugInput}
                    disabled={editing}
                    placeholder="prominent"
                    onChange={(e) => {
                      setSaved(false);
                      setSlugInput(e.target.value);
                    }}
                  />
                </Field>
                <Field label="Organization name (required)" htmlFor="org-name">
                  <Input
                    id="org-name"
                    className="max-w-md"
                    value={name}
                    onChange={(e) => {
                      setSaved(false);
                      setName(e.target.value);
                      // One name: the profile the assistant reads carries the org name.
                      setForm((f) => ({ ...f, name: e.target.value }));
                    }}
                  />
                </Field>
              </div>
            </Card>
          ) : null}

          {["profile", "teams", "glossary", "brand"].includes(section) ? (
            <Card
              id={section}
              title={
                section === "profile"
                  ? "Company details"
                  : section === "teams"
                    ? "Teams"
                    : section === "glossary"
                      ? "Glossary"
                      : "Brand & voice"
              }
            >
              <OrgProfileForm
                value={form}
                section={
                  section === "profile"
                    ? "company"
                    : section === "teams"
                      ? "teams"
                      : section === "glossary"
                        ? "glossary"
                        : "brand"
                }
                onChange={(next) => {
                  setSaved(false);
                  setForm(next);
                }}
              />
            </Card>
          ) : null}

          {["access", "integrations", "bots"].includes(section) ? (
            <Card
              id={section}
              title={
                section === "access"
                  ? "Features"
                  : section === "integrations"
                    ? "Integrations"
                    : "Bots"
              }
            >
              {section === "bots" && slug !== null ? (
                <div className="mb-6 space-y-4">
                  <OrgBotCreate slug={slug} onCreated={() => load(false)} />
                  <OrgBotList
                    slug={slug}
                    bots={detail?.customBots ?? []}
                    onRemoved={() => load(false)}
                  />
                </div>
              ) : null}
              <OrgSwitchesForm
                group={
                  section === "bots"
                    ? "bots"
                    : section === "integrations"
                      ? "integrations"
                      : "features"
                }
                entitlements={switches.entitlements}
                settings={switches.settings}
                slug={slug ?? (slugInput || null)}
                onChange={(next) => {
                  setSaved(false);
                  setSwitches(next);
                }}
              />
            </Card>
          ) : null}

          {section === "signin" ? (
            <Card id="signin" title="Sign-in">
              <div className="flex flex-col gap-4">
                <Field label="Provider" htmlFor="signin-provider">
                  <Select
                    value={signin.provider}
                    onValueChange={(v) =>
                      setSignin({ ...signin, provider: v === "google" ? "google" : "microsoft" })
                    }
                  >
                    <SelectTrigger id="signin-provider" className="max-w-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="microsoft">Microsoft 365</SelectItem>
                      <SelectItem value="google">Google Workspace</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="App" htmlFor="signin-app">
                  <Select
                    value={signin.app}
                    onValueChange={(v) =>
                      setSignin({ ...signin, app: v === "own" ? "own" : "shared" })
                    }
                  >
                    <SelectTrigger id="signin-app" className="max-w-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="shared">Helm customer app (Nymbl-run)</SelectItem>
                      <SelectItem value="own">The company's own app</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                {showsClientId(signin) ? (
                  <Field label="Client id" htmlFor="signin-client">
                    <Input
                      id="signin-client"
                      className="max-w-md"
                      value={signin.clientId ?? ""}
                      onChange={(e) => setSignin({ ...signin, clientId: e.target.value || null })}
                    />
                  </Field>
                ) : null}
                <Field
                  label="Tenant id"
                  htmlFor="signin-tenant"
                  hint="The Directory (tenant) ID from Entra, a GUID. Nymbl's own is recorded on the hub; every other organization needs its own."
                >
                  <Input
                    id="signin-tenant"
                    className="max-w-md"
                    value={signin.tenantId ?? ""}
                    onChange={(e) => setSignin({ ...signin, tenantId: e.target.value || null })}
                  />
                </Field>
                {signinHints(signin, slug, nymblTenantId).map((h) => (
                  <p key={h} className="text-xs text-destructive">
                    {h}
                  </p>
                ))}
                {slug !== null ? (
                  <ConsentBlock slug={slug} signin={detail?.signin ?? signin} />
                ) : null}
                {slug !== null && signin.app === "own" && signin.provider === "microsoft" ? (
                  <SecretBlock slug={slug} />
                ) : null}
              </div>
            </Card>
          ) : null}

          {detail && slug !== null ? (
            <>
              {section === "releases" ? (
                <Card id="releases" title="Release channel">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span>Current channel</span>
                      <Badge variant="outline">
                        {detail.channel === "stable" ? "Stable" : "Early"}
                      </Badge>
                      {detail.pinnedVersion ? (
                        <Badge variant="warning">Pinned to {detail.pinnedVersion}</Badge>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                      <Field label="Channel" htmlFor="org-channel">
                        <Select value={channelChoice} onValueChange={setChannelChoice}>
                          <SelectTrigger id="org-channel" className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="early">Early</SelectItem>
                            <SelectItem value="stable">Stable</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      <Button type="button" variant="outline" onClick={moveChannel}>
                        Move org
                      </Button>
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                      <Field label="Pin the org to a version" htmlFor="org-pin">
                        <Input
                          id="org-pin"
                          className="w-40"
                          value={pinInput}
                          placeholder="v0.50.0"
                          onChange={(e) => setPinInput(e.target.value)}
                        />
                      </Field>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!pinInput.trim()}
                        onClick={() => setPin(pinInput.trim())}
                      >
                        Pin org
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setPin(null)}>
                        Unpin
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Pinning moves this org's unpinned boxes to that version now; after that,
                      channel moves skip the org. Boxes with their own pin are never moved.
                    </p>
                    {releaseNote ? <p className="text-sm">{releaseNote}</p> : null}
                    {releaseError ? (
                      <p className="text-sm text-destructive">{releaseError}</p>
                    ) : null}
                  </div>
                </Card>
              ) : null}

              {section === "people" ? (
                <Card id="people" title="People">
                  <div className="flex flex-col gap-4">
                    <div>
                      <Button asChild>
                        <a href={`/instances/new?org=${encodeURIComponent(slug)}`}>Add a person</a>
                      </Button>
                    </div>
                    <InstanceTable orgSlug={slug} />
                  </div>
                </Card>
              ) : null}

              {section === "history" ? (
                <Card id="history" title="History">
                  {detail.events.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nothing yet.</p>
                  ) : (
                    <ul className="flex flex-col gap-1.5 text-sm">
                      {detail.events.map((e) => (
                        <li key={`${e.at}-${e.action}-${e.actor}`} className="flex flex-wrap gap-2">
                          <span className="font-medium">{e.action}</span>
                          <span className="text-muted-foreground">{e.actor}</span>
                          <span className="text-muted-foreground">{relativeTime(e.at)}</span>
                          {e.detail ? <span className="font-mono text-xs">{e.detail}</span> : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              ) : null}
            </>
          ) : null}
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}
